import { useState, useEffect, useRef } from "react";

const PROXY    = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");

const api = async (path, opts = {}) => {
  const res = await fetch(PROXY + path, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(getToken() ? { Authorization: "Bearer " + getToken() } : {}), ...opts.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const INR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });
const INRc = (n) => "₹" + Math.round(Number(n || 0)).toLocaleString("en-IN");

// ── Business details ──────────────────────────────────────────
const BUSINESS = {
  name:    "Vasudeva Enterprises",
  brand:   "Leather Patta",
  gstin:   "09BDOPB0236M2ZF",
  pan:     "BDOPB0236M",
  address: "Tower 4C- Landcraft Golflinks, Pandav Nagar",
  city:    "Ghaziabad",
  state:   "Uttar Pradesh",
  pincode: "201001",
  phone:   "+91-8287254548",
  email:   "leatherpatta@gmail.com",
  hsn:     "42033000",
  stateCode: "09",
};

const PLATFORMS = [
  { id: "shopify",  name: "Shopify"  },
  { id: "amazon",   name: "Amazon"   },
  { id: "flipkart", name: "Flipkart" },
  { id: "myntra",   name: "Myntra"   },
];

const GST_RATES = [0, 5, 12, 18, 28];

// Resolve the best price from a Shopify line item.
// Shopify can report price=0 for BOGO/discounted/bundled items, so fall back
// to pre-discount fields, the presentment price set, or compare-at price.
function resolveLinePrice(l) {
  const candidates = [
    l.price,
    l.pre_tax_price,
    l.original_price,
    l.price_set && l.price_set.shop_money && l.price_set.shop_money.amount,
    l.original_price_set && l.original_price_set.shop_money && l.original_price_set.shop_money.amount,
    l.compare_at_price,
    l.discounted_price,
  ];
  for (let i = 0; i < candidates.length; i++) {
    const v = parseFloat(candidates[i] || 0);
    if (v > 0) return v;
  }
  return 0;
}

// Resolve customer name across Shopify + marketplace field variations
function resolveCustomerName(o) {
  const c = o.customer || {};
  const addr = o.shipping_address || o.billing_address || {};
  const fromCust = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  const candidates = [
    fromCust,
    c.name,
    addr.name,
    [addr.first_name, addr.last_name].filter(Boolean).join(" ").trim(),
    o.customerName,
    o.buyerName,
    addr.customer_name,
    o.BuyerInfo && o.BuyerInfo.BuyerName,
  ];
  for (let i = 0; i < candidates.length; i++) {
    const v = (candidates[i] || "").trim();
    if (v && v.toLowerCase() !== "undefined undefined" && v !== "undefined") return v;
  }
  return "";
}

// Resolve an address object across field variations
function resolveAddr(o) {
  const a = o.shipping_address || o.billing_address || {};
  return {
    phone:   a.phone || (o.customer && o.customer.phone) || o.phone || "",
    email:   (o.customer && o.customer.email) || o.email || o.contact_email || "",
    address: a.address1 || a.address || a.street || "",
    city:    a.city || "",
    state:   a.province || a.state || a.region || "",
    pincode: a.zip || a.pincode || a.postal_code || "",
    gstin:   a.gstin || o.customerGstin || "",
  };
}

const S = {
  card:    { background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", marginBottom: 14 },
  cardHdr: { padding: "11px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 600, fontSize: 13 },
  btn:     (c = "#F47920") => ({ padding: "7px 16px", borderRadius: 7, fontSize: 12, cursor: "pointer", background: c, color: "#fff", border: "none", fontFamily: "inherit", fontWeight: 600 }),
  btnOut:  { padding: "7px 16px", borderRadius: 7, fontSize: 12, cursor: "pointer", background: "#fff", color: "#374151", border: "1px solid #d1d5db", fontFamily: "inherit" },
  input:   { width: "100%", padding: "7px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, fontFamily: "inherit", outline: "none", marginBottom: 10 },
  label:   { fontSize: 11, color: "#6b7280", fontWeight: 600, display: "block", marginBottom: 3 },
  th:      { padding: "8px 10px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "#6b7280", background: "#f9fafb", whiteSpace: "nowrap" },
  td:      { padding: "7px 10px", borderBottom: "1px solid #f3f4f6", fontSize: 12, verticalAlign: "middle" },
};

// ── number to words ───────────────────────────────────────────
function numberToWords(num) {
  const ones = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
  const tens = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
  function convert(n) {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n/10)] + (n%10 ? " " + ones[n%10] : "");
    if (n < 1000) return ones[Math.floor(n/100)] + " Hundred" + (n%100 ? " " + convert(n%100) : "");
    if (n < 100000) return convert(Math.floor(n/1000)) + " Thousand" + (n%1000 ? " " + convert(n%1000) : "");
    if (n < 10000000) return convert(Math.floor(n/100000)) + " Lakh" + (n%100000 ? " " + convert(n%100000) : "");
    return convert(Math.floor(n/10000000)) + " Crore" + (n%10000000 ? " " + convert(n%10000000) : "");
  }
  const rupees = Math.floor(num);
  const paise  = Math.round((num - rupees) * 100);
  let result = convert(rupees) + " Rupees";
  if (paise > 0) result += " and " + convert(paise) + " Paise";
  return result + " Only";
}

// ── Invoice number generator ──────────────────────────────────
function genInvNumber(type) {
  const d   = new Date();
  const fy  = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  const fyS = String(fy).slice(2) + "-" + String(fy+1).slice(2);
  const seq = String(Math.floor(Math.random() * 9000) + 1000);
  return (type === "proforma" ? "PI" : "LP") + "/" + fyS + "/" + seq;
}

const EMPTY_FORM = {
  type:           "tax",
  invoiceNumber:  genInvNumber("tax"),
  invoiceDate:    new Date().toISOString().split("T")[0],
  dueDate:        "",
  platform:       "shopify",
  // billing
  billingName:    "",
  billingAddress: "",
  billingCity:    "",
  billingState:   "",
  billingPincode: "",
  billingGstin:   "",
  billingPhone:   "",
  billingEmail:   "",
  // shipping
  sameAsShipping: true,
  shippingName:   "",
  shippingAddress:"",
  shippingCity:   "",
  shippingState:  "",
  shippingPincode:"",
  // items
  items: [{ description:"", hsn: BUSINESS.hsn, qty:1, unit:"Pcs", rate:0, gstRate:18 }],
  // other
  notes:          "Thank you for your business!",
  terms:          "Payment due within 7 days. Goods once sold will not be taken back.",
  bankName:       "",
  accountNo:      "",
  ifsc:           "",
  upiId:          "",
  logo:           "",
};

export default function PageInvoicePDF({ toast, products=[], orders=[], invoices=[], tenant, invoiceOrder=null, onClearOrder }) {
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [preview,     setPreview]     = useState(false);
  const [logoBase64,  setLogoBase64]  = useState("");
  const [tab,          setTab]          = useState("create");
  const [allOrders,    setAllOrders]    = useState([]);
  const [loadingOrders,setLoadingOrders]= useState(false);
  const [orderSources, setOrderSources] = useState({});
  const [selOrder,     setSelOrder]     = useState(null);
  const [orderSearch,  setOrderSearch]  = useState("");
  // Retail invoice state
  const [retailForm,   setRetailForm]   = useState({
    customerName:"", customerPhone:"", customerAddress:"", customerGstin:"",
    paymentMode:"Cash", invoiceDate:new Date().toISOString().split("T")[0],
    items:[{ description:"", hsn:"42033000", qty:1, unit:"Pcs", rate:0, gstRate:18 }],
    notes:"Thank you for your purchase!",
  });
  const printRef = useRef(null);

  // Auto-fill from order passed from Orders page
  useEffect(() => {
    if (!invoiceOrder) return;
    const o    = invoiceOrder;
    const addr = o.shipping_address || {};
    const cust = o.customer || {};
    const name = resolveCustomerName(o) || "Guest";
    setForm((f) => ({
      ...f,
      platform:        o._platform || "shopify",
      orderNumber:     o.name || String(o.id || ""),
      customerName:    name,
      customerEmail:   cust.email || "",
      customerPhone:   addr.phone || cust.phone || "",
      customerAddress: [addr.address1, addr.city, addr.province, addr.zip].filter(Boolean).join(", "),
      items: (o.line_items || []).length > 0 ? (o.line_items || []).map((l) => ({
        description: l.name || "",
        hsn:         "42033000",
        qty:         l.quantity || 1,
        unit:        "Pcs",
        rate:        resolveLinePrice(l),
        gstRate:     18,
      })) : f.items,
    }));
    setTab("create");
    if (onClearOrder) onClearOrder();
    toast("Order " + (o.name || "") + " loaded ✓", "success");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceOrder]);

  const setF = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const updateItem = (i, key, val) => setForm((f) => ({
    ...f,
    items: f.items.map((it, idx) => idx === i ? { ...it, [key]: val } : it),
  }));

  const addItem    = () => setForm((f) => ({ ...f, items: [...f.items, { description:"", hsn:BUSINESS.hsn, qty:1, unit:"Pcs", rate:0, gstRate:18 }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_,idx) => idx !== i) }));

  // fill from order
  const fetchAllOrders = async () => {
    setLoadingOrders(true);
    const sources = { shopify: orders.length };
    let all = orders.map((o) => ({ ...o, _platform:"shopify" }));

    const platforms = [
      { id:"amazon",   endpoint:"/amazon/orders",   key:"payload.Orders" },
      { id:"flipkart", endpoint:"/flipkart/orders",  key:"orders" },
      { id:"myntra",   endpoint:"/myntra/orders",    key:"orders" },
      { id:"meesho",   endpoint:"/meesho/orders",    key:"orders" },
    ];

    for (const pl of platforms) {
      try {
        const data = await api(pl.endpoint);
        const raw  = pl.key.split(".").reduce((a,k)=>a?.[k], data) || data.orders || [];
        const norm = raw.map((o) => ({
          id:           o.AmazonOrderId || o.orderId || o.id || "",
          name:         pl.id==="amazon"?"AMZ-"+(o.AmazonOrderId||o.id):
                        pl.id==="flipkart"?"FK-"+(o.orderId||o.id):
                        pl.id==="myntra"?"MYN-"+(o.orderId||o.id):
                        "MSH-"+(o.subOrderNo||o.id||""),
          customer:     { first_name: o.BuyerInfo?.BuyerName || o.customerName || o.shipping_address?.customer_name || "Customer", last_name:"", email:"", phone: o.shipping_address?.phone_number || o.phone || "" },
          shipping_address: { address1: o.ShippingAddress?.AddressLine1||o.shippingAddress||o.shipping_address?.address1||"", city: o.ShippingAddress?.City||o.shipping_address?.city||o.deliveryCity||"", province: o.ShippingAddress?.StateOrRegion||o.shipping_address?.state||"", zip: o.ShippingAddress?.PostalCode||o.shipping_address?.zipcode||"", country:"India" },
          total_price:  o.OrderTotal?.Amount || o.totalAmount || o.price || 0,
          line_items:   (o.items||o.OrderItems||[]).map((l)=>({ name:l.Title||l.title||l.name||"", quantity:parseInt(l.QuantityOrdered||l.quantity||1), price:parseFloat(l.ItemPrice?.Amount||l.sellingPrice||l.price||0), sku:l.SellerSKU||l.skuId||l.sku||"" })),
          financial_status: "paid",
          created_at:   o.PurchaseDate||o.orderDate||new Date().toISOString(),
          _platform:    pl.id,
        }));
        all = [...all, ...norm];
        sources[pl.id] = norm.length;
      } catch { sources[pl.id] = 0; }
    }
    setAllOrders(all);
    setOrderSources(sources);
    setLoadingOrders(false);
  };

  const fillFromPlatformOrder = (o) => {
    const addr = o.shipping_address || {};
    const cust = o.customer || {};
    const name = resolveCustomerName(o);
    setForm((f) => ({
      ...f,
      platform:      o._platform || "shopify",
      orderNumber:   o.name || String(o.id),
      customerName:  name || "Customer",
      customerEmail: cust.email || "",
      customerPhone: cust.phone || addr.phone || "",
      customerAddress:[addr.address1, addr.city, addr.province, addr.zip, addr.country].filter(Boolean).join(", "),
      items: (o.line_items||[]).map((l) => ({
        description: l.name || "",
        hsn:         BUSINESS.hsn,
        qty:         l.quantity || 1,
        unit:        "Pcs",
        rate:        parseFloat(l.price || 0),
        gstRate:     18,
      })) || f.items,
    }));
    setSelOrder(o);
    setTab("create");
    toast("Order loaded — review and generate invoice ✓","success");
  };

  const addRetailItem    = () => setRetailForm((f) => ({ ...f, items:[...f.items, { description:"", hsn:BUSINESS.hsn, qty:1, unit:"Pcs", rate:0, gstRate:18 }] }));
  const removeRetailItem = (i) => setRetailForm((f) => ({ ...f, items:f.items.filter((_,idx)=>idx!==i) }));
  const updateRetailItem = (i,k,v) => setRetailForm((f) => ({ ...f, items:f.items.map((it,idx)=>idx===i?{...it,[k]:v}:it) }));

  const printRetailInvoice = () => {
    const invoiceNo = "RI-" + Date.now().toString(36).toUpperCase();
    const date = new Date(retailForm.invoiceDate).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"});
    let subtotal=0, gstTotal=0;
    const rows = retailForm.items.map((item) => {
      // Prices are tax-inclusive: back-calculate base + GST out of the inclusive price
      const gross = item.qty * item.rate;
      const base  = Math.round(gross / (1 + item.gstRate / 100));
      const gst   = gross - base;
      subtotal  += base; gstTotal += gst;
      return `<tr>
        <td style="padding:7px 10px;border-bottom:1px solid #f3f4f6">${item.description}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f3f4f6;text-align:center">${item.hsn}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f3f4f6;text-align:center">${item.qty} ${item.unit}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f3f4f6;text-align:right">₹${item.rate.toLocaleString("en-IN")}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f3f4f6;text-align:center">${item.gstRate}%</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f3f4f6;text-align:right;font-weight:600">₹${gross.toLocaleString("en-IN")}</td>
      </tr>`;
    }).join("");
    const grand = subtotal + gstTotal;
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Retail Invoice ${invoiceNo}</title>
    <style>*{box-sizing:border-box;margin:0;padding:0;}body{font-family:Arial,sans-serif;color:#111;padding:30px;}
    .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding-bottom:16px;border-bottom:3px solid #F47920;}
    .brand{font-size:22px;font-weight:800;color:#F47920;}.brand-sub{font-size:11px;color:#9ca3af;}
    .inv-title{font-size:18px;font-weight:700;color:#1A3A5C;text-align:right;}.inv-sub{font-size:11px;color:#6b7280;text-align:right;}
    .badge{display:inline-block;background:#F47920;color:#fff;padding:2px 10px;border-radius:20px;font-size:11px;font-weight:700;}
    .grid{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:20px;}
    .box{background:#f9fafb;border-radius:8px;padding:12px 14px;}.box-label{font-size:10px;font-weight:700;color:#9ca3af;text-transform:uppercase;margin-bottom:6px;}
    .box-row{font-size:12px;color:#374151;margin-bottom:3px;}
    table{width:100%;border-collapse:collapse;margin-bottom:16px;}
    thead tr{background:#1A3A5C;}thead th{padding:9px 10px;text-align:left;font-size:11px;color:#fff;font-weight:600;}
    .totals{margin-left:auto;width:260px;background:#f9fafb;border-radius:8px;padding:14px;}
    .trow{display:flex;justify-content:space-between;font-size:13px;margin-bottom:6px;color:#6b7280;}
    .trow span:last-child{color:#111;font-weight:500;}
    .grand{display:flex;justify-content:space-between;font-size:16px;font-weight:800;border-top:2px solid #e5e7eb;padding-top:10px;margin-top:4px;color:#F47920;}
    .footer{margin-top:30px;text-align:center;font-size:11px;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:14px;}
    .retail-badge{background:#15803d;color:#fff;padding:3px 12px;border-radius:20px;font-size:10px;font-weight:700;display:inline-block;margin-top:4px;}
    @media print{body{padding:15px;}}
    </style></head><body>
    <div class="header">
      <div><div class="brand">${BUSINESS.brand}</div><div class="brand-sub">${BUSINESS.name}</div>
        <div style="font-size:11px;color:#6b7280;margin-top:4px">${BUSINESS.address}, ${BUSINESS.city} ${BUSINESS.pincode}</div>
        <div style="font-size:11px;color:#6b7280">GSTIN: ${BUSINESS.gstin} · Ph: ${BUSINESS.phone}</div>
      </div>
      <div><div class="inv-title">RETAIL INVOICE</div><div class="inv-sub">#${invoiceNo}</div>
        <div class="inv-sub">Date: ${date}</div><div class="retail-badge">RETAIL / CASH SALE</div>
      </div>
    </div>
    <div class="grid">
      <div class="box"><div class="box-label">Bill To</div>
        <div class="box-row"><strong>${retailForm.customerName||"Walk-in Customer"}</strong></div>
        ${retailForm.customerPhone?`<div class="box-row">📞 ${retailForm.customerPhone}</div>`:""}
        ${retailForm.customerAddress?`<div class="box-row">${retailForm.customerAddress}</div>`:""}
        ${retailForm.customerGstin?`<div class="box-row">GSTIN: ${retailForm.customerGstin}</div>`:""}
      </div>
      <div class="box"><div class="box-label">Payment Details</div>
        <div class="box-row"><strong>Mode: ${retailForm.paymentMode}</strong></div>
        <div class="box-row">Invoice: #${invoiceNo}</div>
        <div class="box-row">Date: ${date}</div>
      </div>
    </div>
    <table><thead><tr><th>Item</th><th>HSN</th><th style="text-align:center">Qty</th><th style="text-align:right">Rate</th><th style="text-align:center">GST</th><th style="text-align:right">Amount</th></tr></thead>
    <tbody>${rows}</tbody></table>
    <div class="totals">
      <div class="trow"><span>Subtotal</span><span>₹${subtotal.toLocaleString("en-IN")}</span></div>
      <div class="trow"><span>GST</span><span>₹${gstTotal.toLocaleString("en-IN")}</span></div>
      <div class="grand"><span>Total</span><span>₹${grand.toLocaleString("en-IN")}</span></div>
    </div>
    ${retailForm.notes?`<div style="background:#fff8f2;border:1px solid #fed7aa;border-radius:8px;padding:10px 14px;margin-top:16px;font-size:12px;color:#92400e">${retailForm.notes}</div>`:""}
    <div class="footer">${BUSINESS.name} · ${BUSINESS.gstin} · ${BUSINESS.email} · Generated by 3June</div>
    </body></html>`;
    const win = window.open("","_blank");
    win.document.write(html); win.document.close();
    setTimeout(()=>win.print(),600);
    toast("Retail invoice ready to print ✓","success");
  };

  const fillFromOrder = (orderName) => {
    const o = orders.find((x) => x.name === orderName || String(x.id) === orderName);
    if (!o) return;
    const ad = resolveAddr(o);
    setForm((f) => ({
      ...f,
      billingName:    resolveCustomerName(o),
      billingPhone:   ad.phone,
      billingEmail:   ad.email,
      billingAddress: ad.address,
      billingCity:    ad.city,
      billingState:   ad.state,
      billingPincode: ad.pincode,
      billingGstin:   ad.gstin,
      items: (o.line_items || []).map((li) => ({
        description: li.name,
        hsn:         BUSINESS.hsn,
        qty:         li.quantity,
        unit:        "Pcs",
        rate:        Math.round(resolveLinePrice(li)),  // tax-inclusive, with BOGO fallback
        gstRate:     18,
      })),
    }));
    toast("Order details filled ✓", "success");
  };

  // logo upload
  const handleLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setLogoBase64(ev.target.result);
    reader.readAsDataURL(file);
  };

  // calculations
  const calcItem = (item) => {
    // Prices are TAX-INCLUSIVE: rate already includes GST.
    // Back-calculate the taxable (pre-tax) value and the GST out of the inclusive price.
    const gross   = (item.qty || 0) * (item.rate || 0);          // inclusive line total
    const rate    = item.gstRate || 0;
    const taxable = Math.round(gross / (1 + rate / 100) * 100) / 100; // pre-tax value
    const gst     = Math.round((gross - taxable) * 100) / 100;        // GST portion
    return { taxable, gst, total: gross };                        // total = inclusive price
  };

  const totals = form.items.reduce((acc, item) => {
    const c = calcItem(item);
    return { taxable: acc.taxable + c.taxable, gst: acc.gst + c.gst, total: acc.total + c.total };
  }, { taxable: 0, gst: 0, total: 0 });

  const isInterState = form.billingState && form.billingState.toLowerCase() !== BUSINESS.state.toLowerCase();

  // print
  const handlePrint = () => {
    const content = printRef.current?.innerHTML;
    if (!content) return;
    // Open a window exactly 4x6 inches
    const win = window.open("", "_blank", "width=400,height=600");
    win.document.write(`<!DOCTYPE html>
      <html><head><meta charset="UTF-8"><title>${form.invoiceNumber}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: Arial, sans-serif; font-size: 8.5px; color: #111; background:#fff; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 2.5px 4px; border: 0.5px solid #ddd; font-size: 8px; }
        th { background: #f5f5f5; font-weight: 600; }
        .no-border td, .no-border th { border: none; padding: 1.5px 4px; }
        hr { border: none; border-top: 0.5px solid #ddd; margin: 4px 0; }
        @page {
          size: 4in 6in;
          margin: 3mm 4mm;
        }
        @media print {
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          body { margin: 0; }
          html, body { width: 4in; }
        }
      </style>
      </head><body style="width:4in;padding:0;margin:0;">${content}</body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 600);
    toast("4×6 print dialog opened ✓", "success");
  };

  // Compact cell style for the 4x6 invoice items table
  const IC = { padding: "3px 4px", border: "0.5px solid #e5e7eb", fontSize: 7.5, verticalAlign: "top", wordBreak: "break-word" };

  // ── Invoice HTML (used for preview + print) ──────────────────
  const InvoiceHTML = () => {
    const cgst = isInterState ? 0 : totals.gst / 2;
    const sgst = isInterState ? 0 : totals.gst / 2;
    const igst = isInterState ? totals.gst : 0;

    return (
      <div ref={printRef} style={{ fontFamily:"Arial,sans-serif", fontSize:8, color:"#111", width:"100%", maxWidth:"4in", margin:"0 auto", padding:4, background:"#fff", overflow:"hidden" }}>

        {/* header */}
        <table style={{ borderCollapse:"collapse", width:"100%", marginBottom:16 }}>
          <tbody>
            <tr>
              <td style={{ width:"60%", verticalAlign:"top", border:"none", padding:0 }}>
                {logoBase64
                  ? <img src={logoBase64} alt="Logo" style={{ height:36, objectFit:"contain", marginBottom:4 }}/>
                  : <div style={{ fontSize:13, fontWeight:700, color:"#F47920", marginBottom:3 }}>{BUSINESS.brand}</div>}
                <div style={{ fontWeight:700, fontSize:9 }}>{BUSINESS.name}</div>
                <div style={{ fontSize:7.5, color:"#6b7280", marginTop:2, lineHeight:"1.4" }}>
                  {BUSINESS.address}<br/>
                  {BUSINESS.city}, {BUSINESS.state} - {BUSINESS.pincode}<br/>
                  Ph: {BUSINESS.phone} | {BUSINESS.email}<br/>
                  GSTIN: {BUSINESS.gstin} | PAN: {BUSINESS.pan}
                </div>
              </td>
              <td style={{ width:"40%", verticalAlign:"top", border:"none", padding:0, textAlign:"right" }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#F47920", marginBottom:4 }}>
                  {form.type === "proforma" ? "PROFORMA INVOICE" : "TAX INVOICE"}
                </div>
                <table style={{ marginLeft:"auto", borderCollapse:"collapse" }}>
                  <tbody>
                    {[
                      ["Invoice No",  form.invoiceNumber],
                      ["Date",        new Date(form.invoiceDate).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })],
                      ["Platform",    PLATFORMS.find((p) => p.id === form.platform)?.name || form.platform],
                      ...(form.dueDate ? [["Due Date", new Date(form.dueDate).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })]] : []),
                    ].map(([l,v]) => (
                      <tr key={l}>
                        <td style={{ textAlign:"right", padding:"2px 6px", fontWeight:600, fontSize:11, border:"none", color:"#6b7280" }}>{l}:</td>
                        <td style={{ textAlign:"right", padding:"2px 0", fontSize:11, border:"none" }}>{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>

        {/* divider */}
        <div style={{ height:1.5, background:"#F47920", marginBottom:8 }}/>

        {/* billing + shipping */}
        <table style={{ borderCollapse:"collapse", width:"100%", marginBottom:16 }}>
          <tbody>
            <tr>
              <td style={{ width:"50%", verticalAlign:"top", border:"0.5px solid #e5e7eb", padding:"4px 6px" }}>
                <div style={{ fontWeight:700, fontSize:8, color:"#F47920", marginBottom:3, textTransform:"uppercase" }}>Bill To</div>
                <div style={{ fontWeight:600 }}>{form.billingName || "—"}</div>
                <div style={{ color:"#374151", marginTop:3 }}>
                  {form.billingAddress && <div>{form.billingAddress}</div>}
                  {(form.billingCity || form.billingState) && <div>{[form.billingCity, form.billingState, form.billingPincode].filter(Boolean).join(", ")}</div>}
                  {form.billingPhone && <div>Ph: {form.billingPhone}</div>}
                  {form.billingEmail && <div>{form.billingEmail}</div>}
                  {form.billingGstin && <div>GSTIN: {form.billingGstin}</div>}
                </div>
              </td>
              <td style={{ width:"50%", verticalAlign:"top", border:"0.5px solid #e5e7eb", padding:"4px 6px" }}>
                <div style={{ fontWeight:700, fontSize:8, color:"#F47920", marginBottom:3, textTransform:"uppercase" }}>Ship To</div>
                {form.sameAsShipping ? (
                  <div style={{ color:"#6b7280", fontStyle:"italic" }}>Same as billing address</div>
                ) : (
                  <div>
                    <div style={{ fontWeight:600 }}>{form.shippingName || form.billingName}</div>
                    <div>{form.shippingAddress}</div>
                    <div>{[form.shippingCity, form.shippingState, form.shippingPincode].filter(Boolean).join(", ")}</div>
                  </div>
                )}
              </td>
            </tr>
          </tbody>
        </table>

        {/* items table */}
        <table style={{ borderCollapse:"collapse", width:"100%", marginBottom:16, tableLayout:"fixed" }}>
          <colgroup>
            <col style={{ width:"4%" }}/>
            <col style={{ width:"30%" }}/>
            <col style={{ width:"11%" }}/>
            <col style={{ width:"5%" }}/>
            <col style={{ width:"6%" }}/>
            <col style={{ width:"12%" }}/>
            <col style={{ width:"11%" }}/>
            <col style={{ width:"6%" }}/>
            <col style={{ width:"8%" }}/>
            <col style={{ width:"12%" }}/>
          </colgroup>
          <thead>
            <tr style={{ background:"#F47920" }}>
              {["#","Description","HSN","Qty","Unit","Rate incl.","Taxable","GST%","GST","Total"].map((h) => (
                <th key={h} style={{ padding:"3px 3px", color:"#fff", fontWeight:600, fontSize:7.5, textAlign: h==="Description"?"left":"center", border:"0.5px solid #e5870d", wordBreak:"break-word" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {form.items.map((item, i) => {
              const c = calcItem(item);
              return (
                <tr key={i} style={{ background: i%2===0?"#fff":"#fffbf5" }}>
                  <td style={{ ...IC, textAlign:"center" }}>{i+1}</td>
                  <td style={{ ...IC, lineHeight:"1.25" }}>{item.description || "—"}</td>
                  <td style={{ ...IC, textAlign:"center", fontFamily:"monospace", fontSize:6.5 }}>{item.hsn}</td>
                  <td style={{ ...IC, textAlign:"center" }}>{item.qty}</td>
                  <td style={{ ...IC, textAlign:"center" }}>{item.unit}</td>
                  <td style={{ ...IC, textAlign:"right" }}>{INRc(item.rate)}</td>
                  <td style={{ ...IC, textAlign:"right" }}>{INRc(c.taxable)}</td>
                  <td style={{ ...IC, textAlign:"center" }}>{item.gstRate}%</td>
                  <td style={{ ...IC, textAlign:"right" }}>{INRc(c.gst)}</td>
                  <td style={{ ...IC, textAlign:"right", fontWeight:600 }}>{INRc(c.total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* totals + GST summary */}
        <table style={{ borderCollapse:"collapse", width:"100%", marginBottom:16 }}>
          <tbody>
            <tr>
              {/* GST summary */}
              <td style={{ width:"55%", verticalAlign:"top", border:"none", padding:0, paddingRight:16 }}>
                <table style={{ borderCollapse:"collapse", width:"100%", fontSize:7.5, tableLayout:"fixed" }}>
                  <thead>
                    <tr style={{ background:"#f9fafb" }}>
                      <th style={{ border:"0.5px solid #e5e7eb", padding:"2px 3px" }}>HSN</th>
                      <th style={{ border:"0.5px solid #e5e7eb", padding:"2px 3px" }}>Taxable</th>
                      {!isInterState ? (
                        <>
                          <th style={{ border:"0.5px solid #e5e7eb", padding:"2px 3px" }}>CGST</th>
                          <th style={{ border:"0.5px solid #e5e7eb", padding:"2px 3px" }}>SGST</th>
                        </>
                      ) : (
                        <th style={{ border:"0.5px solid #e5e7eb", padding:"2px 3px" }}>IGST</th>
                      )}
                      <th style={{ border:"0.5px solid #e5e7eb", padding:"2px 3px" }}>Total Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((item, i) => {
                      const c = calcItem(item);
                      return (
                        <tr key={i}>
                          <td style={{ border:"0.5px solid #e5e7eb", padding:"2px 3px", fontFamily:"monospace", fontSize:6.5 }}>{item.hsn}</td>
                          <td style={{ border:"0.5px solid #e5e7eb", padding:"2px 3px", textAlign:"right" }}>{INRc(c.taxable)}</td>
                          {!isInterState ? (
                            <>
                              <td style={{ border:"0.5px solid #e5e7eb", padding:"2px 3px", textAlign:"right" }}>{INRc(c.gst/2)}</td>
                              <td style={{ border:"0.5px solid #e5e7eb", padding:"2px 3px", textAlign:"right" }}>{INRc(c.gst/2)}</td>
                            </>
                          ) : (
                            <td style={{ border:"0.5px solid #e5e7eb", padding:"2px 3px", textAlign:"right" }}>{INRc(c.gst)}</td>
                          )}
                          <td style={{ border:"0.5px solid #e5e7eb", padding:"2px 3px", textAlign:"right", fontWeight:600 }}>{INRc(c.gst)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* amount in words */}
                <div style={{ marginTop:10, padding:"8px 10px", background:"#fffbf5", border:"1px solid #fed7aa", borderRadius:6, fontSize:11 }}>
                  <span style={{ fontWeight:600 }}>Amount in words: </span>
                  {numberToWords(totals.total)}
                </div>
              </td>

              {/* totals */}
              <td style={{ width:"45%", verticalAlign:"top", border:"none", padding:0 }}>
                <table style={{ borderCollapse:"collapse", width:"100%", fontSize:12 }}>
                  <tbody>
                    {[
                      ["Subtotal (Taxable)", INR(totals.taxable), false],
                      ...(!isInterState ? [
                        ["CGST", INR(cgst), false],
                        ["SGST", INR(sgst), false],
                      ] : [
                        ["IGST", INR(igst), false],
                      ]),
                      ["Round Off", INR(Math.round(totals.total) - totals.total), false],
                    ].map(([l,v]) => (
                      <tr key={l}>
                        <td style={{ border:"1px solid #e5e7eb", padding:"6px 10px", color:"#6b7280" }}>{l}</td>
                        <td style={{ border:"1px solid #e5e7eb", padding:"6px 10px", textAlign:"right" }}>{v}</td>
                      </tr>
                    ))}
                    <tr style={{ background:"#F47920" }}>
                      <td style={{ border:"1px solid #e5870d", padding:"8px 10px", fontWeight:700, color:"#fff", fontSize:13 }}>Grand Total</td>
                      <td style={{ border:"1px solid #e5870d", padding:"8px 10px", textAlign:"right", fontWeight:700, color:"#fff", fontSize:13 }}>{INR(Math.round(totals.total))}</td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>

        {/* bank details */}
        {(form.bankName || form.upiId) && (
          <div style={{ border:"1px solid #e5e7eb", borderRadius:6, padding:"10px 12px", marginBottom:14, fontSize:11 }}>
            <div style={{ fontWeight:700, marginBottom:6, color:"#F47920" }}>Payment Details</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {form.bankName   && <div><span style={{ color:"#6b7280" }}>Bank: </span>{form.bankName}</div>}
              {form.accountNo  && <div><span style={{ color:"#6b7280" }}>Account: </span>{form.accountNo}</div>}
              {form.ifsc       && <div><span style={{ color:"#6b7280" }}>IFSC: </span>{form.ifsc}</div>}
              {form.upiId      && <div><span style={{ color:"#6b7280" }}>UPI: </span>{form.upiId}</div>}
            </div>
          </div>
        )}

        {/* notes + terms */}
        <table style={{ borderCollapse:"collapse", width:"100%", marginBottom:16, fontSize:11 }}>
          <tbody><tr>
            {form.notes && (
              <td style={{ width:"50%", border:"1px solid #e5e7eb", padding:"8px 10px", verticalAlign:"top" }}>
                <div style={{ fontWeight:700, marginBottom:4 }}>Notes</div>
                <div style={{ color:"#6b7280" }}>{form.notes}</div>
              </td>
            )}
            {form.terms && (
              <td style={{ width:"50%", border:"1px solid #e5e7eb", padding:"8px 10px", verticalAlign:"top" }}>
                <div style={{ fontWeight:700, marginBottom:4 }}>Terms & Conditions</div>
                <div style={{ color:"#6b7280" }}>{form.terms}</div>
              </td>
            )}
          </tr></tbody>
        </table>

        {/* signature */}
        <table style={{ borderCollapse:"collapse", width:"100%" }}>
          <tbody><tr>
            <td style={{ border:"none", padding:0, width:"60%" }}>
              <div style={{ fontSize:10, color:"#9ca3af" }}>This is a computer generated invoice and does not require a physical signature.</div>
            </td>
            <td style={{ border:"1px solid #e5e7eb", padding:"8px 12px", width:"40%", textAlign:"center" }}>
              <div style={{ height:40 }}/>
              <div style={{ borderTop:"1px solid #374151", paddingTop:4, fontWeight:600, fontSize:11 }}>
                For {BUSINESS.name}
              </div>
              <div style={{ fontSize:10, color:"#9ca3af" }}>Authorised Signatory</div>
            </td>
          </tr></tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      {/* tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:16 }}>
        {[{id:"platform",label:"📦 From Orders"},{id:"create",label:"✏️ Manual Invoice"},{id:"retail",label:"🏪 Retail Invoice"},{id:"history",label:"📋 History ("+invoices.length+")"}].map((t)=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{ padding:"10px 20px", background:"none", border:"none", borderBottom:tab===t.id?"2px solid #F47920":"2px solid transparent", color:tab===t.id?"#F47920":"#6b7280", fontWeight:tab===t.id?600:400, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>{t.label}</button>
        ))}
      </div>

      {/* ══ FROM PLATFORM ORDERS ══ */}
      {tab === "platform" && (
        <div>
          <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
            <button style={S.btn()} onClick={fetchAllOrders} disabled={loadingOrders}>
              {loadingOrders ? "⏳ Loading…" : "↻ Fetch Orders from All Platforms"}
            </button>
            {allOrders.length > 0 && (
              <>
                <input style={{ flex:1, minWidth:160, padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit" }}
                  placeholder="Search order, customer…" value={orderSearch} onChange={(e)=>setOrderSearch(e.target.value)}/>
                <span style={{ fontSize:12, color:"#6b7280" }}>{allOrders.length} orders</span>
              </>
            )}
          </div>

          {/* Platform source counts */}
          {Object.keys(orderSources).length > 0 && (
            <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
              {Object.entries(orderSources).map(([pl,count])=>{
                const colors={shopify:"#5a8a00",amazon:"#c45000",flipkart:"#1b57c0",myntra:"#b5003c",meesho:"#9f2089"};
                const icons={shopify:"🏪",amazon:"📦",flipkart:"🛒",myntra:"👗",meesho:"🛍️"};
                return count>0?(
                  <span key={pl} style={{ background:(colors[pl]||"#6b7280")+"18", color:colors[pl]||"#6b7280", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700 }}>
                    {icons[pl]||"•"} {pl.charAt(0).toUpperCase()+pl.slice(1)}: {count}
                  </span>
                ):null;
              })}
            </div>
          )}

          {allOrders.length === 0 && !loadingOrders && (
            <div style={{ ...S.card, padding:60, textAlign:"center" }}>
              <div style={{ fontSize:48, marginBottom:16 }}>📦</div>
              <div style={{ fontWeight:700, fontSize:16, marginBottom:8 }}>Fetch orders from all platforms</div>
              <div style={{ fontSize:13, color:"#9ca3af", marginBottom:20 }}>Shopify, Amazon, Flipkart, Myntra and Meesho orders in one place — select any to generate an invoice</div>
              <button style={S.btn()} onClick={fetchAllOrders}>↻ Load Orders</button>
            </div>
          )}

          {allOrders.length > 0 && (
            <div style={S.card}>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>
                    {["Platform","Order","Date","Customer","Items","Total",""].map((h)=>(
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {allOrders.filter((o)=>{
                      if (!orderSearch) return true;
                      const q = orderSearch.toLowerCase();
                      return (o.name||"").toLowerCase().includes(q) ||
                             ((o.customer?.first_name||"")+" "+(o.customer?.last_name||"")).toLowerCase().includes(q);
                    }).map((o,i) => {
                      const colors={shopify:"#5a8a00",amazon:"#c45000",flipkart:"#1b57c0",myntra:"#b5003c",meesho:"#9f2089"};
                      const icons={shopify:"🏪",amazon:"📦",flipkart:"🛒",myntra:"👗",meesho:"🛍️"};
                      const pl = o._platform||"shopify";
                      return (
                        <tr key={i} onMouseEnter={(e)=>e.currentTarget.style.background="#fafafa"} onMouseLeave={(e)=>e.currentTarget.style.background=""}>
                          <td style={S.td}>
                            <span style={{ background:(colors[pl]||"#6b7280")+"18", color:colors[pl]||"#6b7280", padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700 }}>
                              {icons[pl]||"•"} {pl.charAt(0).toUpperCase()+pl.slice(1)}
                            </span>
                          </td>
                          <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920", fontWeight:600 }}>{o.name}</td>
                          <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{new Date(o.created_at).toLocaleDateString("en-IN")}</td>
                          <td style={{ ...S.td, fontWeight:500 }}>{o.customer?.first_name} {o.customer?.last_name}</td>
                          <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{(o.line_items||[]).length} items</td>
                          <td style={{ ...S.td, fontWeight:700 }}>₹{parseFloat(o.total_price||0).toLocaleString("en-IN")}</td>
                          <td style={S.td}>
                            <button style={S.btn()} onClick={()=>fillFromPlatformOrder(o)}>
                              🧾 Generate Invoice
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ RETAIL INVOICE ══ */}
      {tab === "retail" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          {/* Left — form */}
          <div>
            <div style={S.card}>
              <div style={S.cardHdr}><span>🏪 Retail / Cash Invoice</span></div>
              <div style={{ padding:16 }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  <div>
                    <label style={S.label}>Customer Name</label>
                    <input style={S.input} placeholder="Walk-in Customer" value={retailForm.customerName}
                      onChange={(e)=>setRetailForm((f)=>({...f,customerName:e.target.value}))}/>
                  </div>
                  <div>
                    <label style={S.label}>Phone</label>
                    <input style={S.input} placeholder="+91 98765 43210" value={retailForm.customerPhone}
                      onChange={(e)=>setRetailForm((f)=>({...f,customerPhone:e.target.value}))}/>
                  </div>
                  <div>
                    <label style={S.label}>Invoice Date</label>
                    <input type="date" style={S.input} value={retailForm.invoiceDate}
                      onChange={(e)=>setRetailForm((f)=>({...f,invoiceDate:e.target.value}))}/>
                  </div>
                  <div>
                    <label style={S.label}>Payment Mode</label>
                    <select style={S.input} value={retailForm.paymentMode}
                      onChange={(e)=>setRetailForm((f)=>({...f,paymentMode:e.target.value}))}>
                      {["Cash","UPI","Card","NEFT","Cheque"].map((m)=><option key={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label style={S.label}>Address</label>
                  <input style={S.input} placeholder="Customer address (optional)" value={retailForm.customerAddress}
                    onChange={(e)=>setRetailForm((f)=>({...f,customerAddress:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>Customer GSTIN (optional — for B2B)</label>
                  <input style={S.input} placeholder="27XXXXXXXXXXXXXXX" value={retailForm.customerGstin}
                    onChange={(e)=>setRetailForm((f)=>({...f,customerGstin:e.target.value}))}/>
                </div>

                {/* Items */}
                <div style={{ fontWeight:600, fontSize:13, margin:"10px 0 8px" }}>Items</div>
                {retailForm.items.map((item,i)=>(
                  <div key={i} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr auto", gap:6, marginBottom:8, alignItems:"center" }}>
                    <input style={{ ...S.input, marginBottom:0 }} placeholder="Description" value={item.description}
                      onChange={(e)=>updateRetailItem(i,"description",e.target.value)}/>
                    <input type="number" style={{ ...S.input, marginBottom:0 }} placeholder="Qty" min="1" value={item.qty}
                      onChange={(e)=>updateRetailItem(i,"qty",parseInt(e.target.value)||1)}/>
                    <input type="number" style={{ ...S.input, marginBottom:0 }} placeholder="Rate ₹" value={item.rate}
                      onChange={(e)=>updateRetailItem(i,"rate",parseFloat(e.target.value)||0)}/>
                    <select style={{ ...S.input, marginBottom:0 }} value={item.gstRate}
                      onChange={(e)=>updateRetailItem(i,"gstRate",parseFloat(e.target.value))}>
                      {GST_RATES.map((r)=><option key={r} value={r}>{r}%</option>)}
                    </select>
                    <div style={{ fontSize:12, fontWeight:600, color:"#F47920" }}>
                      ₹{(item.qty*item.rate*(1+item.gstRate/100)).toFixed(0)}
                    </div>
                    {retailForm.items.length > 1 && (
                      <button onClick={()=>removeRetailItem(i)} style={{ background:"none", border:"none", color:"#dc2626", cursor:"pointer", fontSize:18 }}>×</button>
                    )}
                  </div>
                ))}
                <button style={{ ...S.btnOut, fontSize:12, marginBottom:12 }} onClick={addRetailItem}>+ Add Item</button>

                {/* Totals preview */}
                <div style={{ background:"#f9fafb", borderRadius:8, padding:"10px 14px", marginBottom:12 }}>
                  {[
                    ["Subtotal", retailForm.items.reduce((a,it)=>a+it.qty*it.rate,0)],
                    ["GST", retailForm.items.reduce((a,it)=>a+Math.round(it.qty*it.rate*it.gstRate/100),0)],
                  ].map(([l,v])=>(
                    <div key={l} style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:4 }}>
                      <span style={{ color:"#6b7280" }}>{l}</span>
                      <span>₹{v.toLocaleString("en-IN")}</span>
                    </div>
                  ))}
                  <div style={{ display:"flex", justifyContent:"space-between", fontWeight:800, fontSize:15, borderTop:"1px solid #e5e7eb", paddingTop:8, marginTop:4, color:"#F47920" }}>
                    <span>Total</span>
                    <span>₹{(retailForm.items.reduce((a,it)=>a+it.qty*it.rate*(1+it.gstRate/100),0)).toLocaleString("en-IN",{maximumFractionDigits:0})}</span>
                  </div>
                </div>

                <div>
                  <label style={S.label}>Notes</label>
                  <input style={S.input} value={retailForm.notes} onChange={(e)=>setRetailForm((f)=>({...f,notes:e.target.value}))}/>
                </div>

                <button style={{ ...S.btn(), width:"100%", padding:11, fontSize:14 }} onClick={printRetailInvoice}>
                  🖨️ Print / Download Retail Invoice
                </button>
              </div>
            </div>
          </div>

          {/* Right — preview */}
          <div>
            <div style={S.card}>
              <div style={S.cardHdr}><span>Preview</span></div>
              <div style={{ padding:16 }}>
                <div style={{ background:"#f9fafb", borderRadius:8, padding:16, fontSize:12 }}>
                  <div style={{ fontWeight:800, fontSize:16, color:"#F47920", marginBottom:2 }}>{BUSINESS.brand}</div>
                  <div style={{ fontSize:11, color:"#6b7280", marginBottom:10 }}>{BUSINESS.name} · {BUSINESS.gstin}</div>
                  <div style={{ borderTop:"2px solid #F47920", paddingTop:10, marginBottom:10 }}>
                    <div style={{ fontWeight:700 }}>RETAIL INVOICE</div>
                    <div style={{ color:"#6b7280", fontSize:11 }}>Date: {new Date(retailForm.invoiceDate).toLocaleDateString("en-IN")}</div>
                  </div>
                  <div style={{ marginBottom:10 }}>
                    <div style={{ fontWeight:600 }}>{retailForm.customerName||"Walk-in Customer"}</div>
                    {retailForm.customerPhone && <div style={{ color:"#6b7280", fontSize:11 }}>📞 {retailForm.customerPhone}</div>}
                    {retailForm.customerAddress && <div style={{ color:"#6b7280", fontSize:11 }}>{retailForm.customerAddress}</div>}
                  </div>
                  <div style={{ background:"#fff", borderRadius:6, padding:"8px 10px", marginBottom:10 }}>
                    {retailForm.items.map((it,i)=>(
                      <div key={i} style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:4 }}>
                        <span>{it.description||"Item "+(i+1)} ×{it.qty}</span>
                        <span style={{ fontWeight:600 }}>₹{(it.qty*it.rate*(1+it.gstRate/100)).toFixed(0)}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", fontWeight:800, color:"#F47920", fontSize:14 }}>
                    <span>Total</span>
                    <span>₹{(retailForm.items.reduce((a,it)=>a+it.qty*it.rate*(1+it.gstRate/100),0)).toLocaleString("en-IN",{maximumFractionDigits:0})}</span>
                  </div>
                  <div style={{ marginTop:10, fontSize:10, color:"#9ca3af", textAlign:"center" }}>Payment: {retailForm.paymentMode}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ FROM ORDERS TAB ══ */}
      {tab==="platform" && (
        <div>
          <div style={{ background:"#f0fdf4", borderRadius:8, padding:"12px 16px", marginBottom:14, fontSize:12, color:"#15803d" }}>
            💡 Click <strong>"Generate Invoice"</strong> on any order below to auto-fill all customer details, address, products and amounts into the invoice form.
          </div>
          <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden" }}>
            <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>📦 Recent Orders — Click to Generate Invoice</div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr>{["Order","Date","Customer","Phone","City","Platform","Amount","Items",""].map(h=>(
                    <th key={h} style={{ padding:"7px 12px", textAlign:"left", fontSize:10, fontWeight:700, color:"#9ca3af", background:"#f9fafb", whiteSpace:"nowrap" }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr><td colSpan={9} style={{ padding:40, textAlign:"center", color:"#9ca3af" }}>No orders synced yet</td></tr>
                  ) : orders.slice(0,50).map((o,i) => {
                    const cust    = o.customer || {};
                    const addr    = o.shipping_address || {};
                    const name    = [cust.first_name, cust.last_name].filter(Boolean).join(" ") || "Guest";
                    const phone   = addr.phone || cust.phone || "—";
                    const city    = addr.city || "—";
                    const items   = (o.line_items||[]).length || 1;
                    const platColors = { shopify:"#5a8a00", amazon:"#c45000", flipkart:"#1b57c0", myntra:"#b5003c", meesho:"#9f2089" };
                    const platColor  = platColors[o._platform||"shopify"] || "#6b7280";
                    return (
                      <tr key={o.id||o.name} style={{ background:i%2?"#fafafa":"#fff" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#fff8f2"}
                        onMouseLeave={e=>e.currentTarget.style.background=i%2?"#fafafa":"#fff"}>
                        <td style={{ padding:"8px 12px", fontFamily:"monospace", fontSize:11, color:"#F47920", fontWeight:600 }}>{o.name||o.id}</td>
                        <td style={{ padding:"8px 12px", fontSize:11, color:"#6b7280" }}>{o.created_at?.slice(0,10)||"—"}</td>
                        <td style={{ padding:"8px 12px", fontSize:12, fontWeight:500 }}>{name}</td>
                        <td style={{ padding:"8px 12px", fontSize:11, fontFamily:"monospace" }}>{phone}</td>
                        <td style={{ padding:"8px 12px", fontSize:11 }}>{city}</td>
                        <td style={{ padding:"8px 12px" }}>
                          <span style={{ fontSize:9, padding:"2px 7px", borderRadius:10, background:platColor+"18", color:platColor, fontWeight:700, textTransform:"capitalize" }}>
                            {o._platform||"shopify"}
                          </span>
                        </td>
                        <td style={{ padding:"8px 12px", fontWeight:600, fontSize:12 }}>₹{parseFloat(o.total_price||0).toLocaleString("en-IN")}</td>
                        <td style={{ padding:"8px 12px", fontSize:11, color:"#6b7280" }}>{items} item{items!==1?"s":""}</td>
                        <td style={{ padding:"8px 12px" }}>
                          <button
                            style={{ padding:"5px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#F47920", color:"#fff", border:"none", fontFamily:"inherit", fontWeight:700 }}
                            onClick={() => {
                              // Auto-fill form from this order
                              const a    = o.shipping_address || {};
                              const cu   = o.customer || {};
                              const nm   = [cu.first_name, cu.last_name].filter(Boolean).join(" ") || "Guest";
                              setForm(f => ({
                                ...f,
                                platform:        o._platform || "shopify",
                                orderNumber:     o.name || String(o.id || ""),
                                customerName:    nm,
                                customerEmail:   cu.email || "",
                                customerPhone:   a.phone || cu.phone || "",
                                customerAddress: [a.address1, a.address2, a.city, a.province, a.zip].filter(Boolean).join(", "),
                                items: (o.line_items||[]).length > 0
                                  ? o.line_items.map(l => ({
                                      description: l.name || l.title || "",
                                      hsn:         "42033000",
                                      qty:         l.quantity || 1,
                                      unit:        "Pcs",
                                      rate:        parseFloat(l.price || 0),
                                      gstRate:     12,
                                    }))
                                  : f.items,
                              }));
                              setTab("create");
                              toast("✅ Order " + (o.name||o.id||"") + " loaded — review and generate", "success");
                            }}>
                            🧾 Generate Invoice
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab==="create" && (
        <div>
          {invoiceOrder && (
            <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"10px 16px", marginBottom:12, display:"flex", alignItems:"center", justifyContent:"space-between", fontSize:12 }}>
              <span>✅ <strong>Auto-filled from order {invoiceOrder.name||invoiceOrder.id}</strong>{" — "}{([invoiceOrder.customer?.first_name, invoiceOrder.customer?.last_name].filter(Boolean).join(" "))||"Guest"}{" · "}{invoiceOrder.shipping_address?.city||""}</span>
              <button style={{ background:"none", border:"none", fontSize:11, color:"#15803d", cursor:"pointer", fontFamily:"inherit", fontWeight:600 }} onClick={()=>{ if(onClearOrder) onClearOrder(); }}>Clear ×</button>
            </div>
          )}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          {/* LEFT — form */}
          <div>
            {/* invoice type + number */}
            <div style={S.card}>
              <div style={S.cardHdr}>Invoice Details</div>
              <div style={{ padding:16 }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  <div>
                    <label style={S.label}>Invoice Type</label>
                    <select style={S.input} value={form.type} onChange={(e)=>{ setF("type",e.target.value); setF("invoiceNumber",genInvNumber(e.target.value)); }}>
                      <option value="tax">Tax Invoice</option>
                      <option value="proforma">Proforma Invoice</option>
                    </select>
                  </div>
                  <div>
                    <label style={S.label}>Invoice Number</label>
                    <input style={S.input} value={form.invoiceNumber} onChange={(e)=>setF("invoiceNumber",e.target.value)}/>
                  </div>
                  <div>
                    <label style={S.label}>Invoice Date</label>
                    <input type="date" style={S.input} value={form.invoiceDate} onChange={(e)=>setF("invoiceDate",e.target.value)}/>
                  </div>
                  <div>
                    <label style={S.label}>Due Date (optional)</label>
                    <input type="date" style={S.input} value={form.dueDate} onChange={(e)=>setF("dueDate",e.target.value)}/>
                  </div>
                  <div>
                    <label style={S.label}>Platform</label>
                    <select style={S.input} value={form.platform} onChange={(e)=>setF("platform",e.target.value)}>
                      {PLATFORMS.map((p)=><option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={S.label}>Fill from Order</label>
                    <input style={S.input} placeholder="Order #" list="ord-list" onChange={(e)=>fillFromOrder(e.target.value)}/>
                    <datalist id="ord-list">{orders.map((o,i)=><option key={i} value={o.name||o.id}/>)}</datalist>
                  </div>
                </div>

                <label style={S.label}>Upload Logo</label>
                <input type="file" accept="image/*" onChange={handleLogo} style={{ marginBottom:10, fontSize:12 }}/>
                {logoBase64 && <img src={logoBase64} alt="logo" style={{ height:40, objectFit:"contain", marginBottom:10 }}/>}
              </div>
            </div>

            {/* billing details */}
            <div style={S.card}>
              <div style={S.cardHdr}>Customer Details</div>
              <div style={{ padding:16 }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  {[
                    {label:"Name *",    key:"billingName",    placeholder:"Customer name"},
                    {label:"Phone",     key:"billingPhone",   placeholder:"98XXXXXXXX"},
                    {label:"Email",     key:"billingEmail",   placeholder:"customer@email.com"},
                    {label:"GSTIN",     key:"billingGstin",   placeholder:"22AAAAA0000A1Z5"},
                  ].map(({label,key,placeholder})=>(
                    <div key={key}>
                      <label style={S.label}>{label}</label>
                      <input style={S.input} placeholder={placeholder} value={form[key]} onChange={(e)=>setF(key,e.target.value)}/>
                    </div>
                  ))}
                </div>
                <label style={S.label}>Address</label>
                <input style={S.input} placeholder="Street address" value={form.billingAddress} onChange={(e)=>setF("billingAddress",e.target.value)}/>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
                  {[{label:"City",key:"billingCity"},{label:"State",key:"billingState"},{label:"Pincode",key:"billingPincode"}].map(({label,key})=>(
                    <div key={key}>
                      <label style={S.label}>{label}</label>
                      <input style={S.input} value={form[key]} onChange={(e)=>setF(key,e.target.value)}/>
                    </div>
                  ))}
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                  <input type="checkbox" id="same" checked={form.sameAsShipping} onChange={(e)=>setF("sameAsShipping",e.target.checked)}/>
                  <label htmlFor="same" style={{ fontSize:12, cursor:"pointer" }}>Shipping address same as billing</label>
                </div>
                {!form.sameAsShipping && (
                  <div style={{ background:"#f9fafb", borderRadius:8, padding:12 }}>
                    <label style={{ ...S.label, marginBottom:8 }}>Shipping Address</label>
                    {[{label:"Name",key:"shippingName"},{label:"Address",key:"shippingAddress"},{label:"City",key:"shippingCity"},{label:"State",key:"shippingState"},{label:"Pincode",key:"shippingPincode"}].map(({label,key})=>(
                      <div key={key}>
                        <label style={S.label}>{label}</label>
                        <input style={S.input} value={form[key]} onChange={(e)=>setF(key,e.target.value)}/>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* bank details */}
            <div style={S.card}>
              <div style={S.cardHdr}>Payment Details (optional)</div>
              <div style={{ padding:16 }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  {[{label:"Bank Name",key:"bankName"},{label:"Account No",key:"accountNo"},{label:"IFSC Code",key:"ifsc"},{label:"UPI ID",key:"upiId"}].map(({label,key})=>(
                    <div key={key}>
                      <label style={S.label}>{label}</label>
                      <input style={S.input} value={form[key]} onChange={(e)=>setF(key,e.target.value)}/>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* notes */}
            <div style={S.card}>
              <div style={S.cardHdr}>Notes & Terms</div>
              <div style={{ padding:16 }}>
                <label style={S.label}>Notes</label>
                <textarea style={{ ...S.input, height:60, resize:"vertical" }} value={form.notes} onChange={(e)=>setF("notes",e.target.value)}/>
                <label style={S.label}>Terms & Conditions</label>
                <textarea style={{ ...S.input, height:60, resize:"vertical" }} value={form.terms} onChange={(e)=>setF("terms",e.target.value)}/>
              </div>
            </div>
          </div>

          {/* RIGHT — items + preview */}
          <div>
            {/* items */}
            <div style={S.card}>
              <div style={S.cardHdr}>
                <span>Line Items</span>
                <button style={S.btn()} onClick={addItem}>+ Add Item</button>
              </div>
              <div style={{ padding:16 }}>
                {form.items.map((item,i)=>(
                  <div key={i} style={{ background:"#f9fafb", borderRadius:8, padding:12, marginBottom:10, position:"relative" }}>
                    <button onClick={()=>removeItem(i)} style={{ position:"absolute", top:8, right:8, background:"none", border:"none", color:"#dc2626", cursor:"pointer", fontSize:16 }}>✕</button>
                    <label style={S.label}>Description *</label>
                    <input style={S.input} placeholder="Product name / description" value={item.description}
                      onChange={(e)=>updateItem(i,"description",e.target.value)} list="prod-list"/>
                    <datalist id="prod-list">{products.map((p)=><option key={p.id} value={p.name}/>)}</datalist>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:8 }}>
                      {[{label:"HSN",key:"hsn"},{label:"Qty",key:"qty",type:"number"},{label:"Unit",key:"unit"},{label:"Rate (₹)",key:"rate",type:"number"},{label:"GST%",key:"gstRate"}].map(({label,key,type})=>(
                        <div key={key}>
                          <label style={S.label}>{label}</label>
                          {key==="unit" ? (
                            <select style={{ ...S.input }} value={item[key]} onChange={(e)=>updateItem(i,key,e.target.value)}>
                              {["Pcs","Kg","Meter","Box","Dozen","Set"].map((u)=><option key={u}>{u}</option>)}
                            </select>
                          ) : key==="gstRate" ? (
                            <select style={{ ...S.input }} value={item[key]} onChange={(e)=>updateItem(i,key,parseInt(e.target.value))}>
                              {GST_RATES.map((r)=><option key={r} value={r}>{r}%</option>)}
                            </select>
                          ) : (
                            <input type={type||"text"} style={S.input} value={item[key]} onChange={(e)=>updateItem(i,key,type==="number"?parseFloat(e.target.value)||0:e.target.value)}/>
                          )}
                        </div>
                      ))}
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"#6b7280", marginTop:4 }}>
                      <span>Taxable: {INR(calcItem(item).taxable)}</span>
                      <span>GST: {INR(calcItem(item).gst)}</span>
                      <span style={{ fontWeight:700, color:"#F47920" }}>Total: {INR(calcItem(item).total)}</span>
                    </div>
                  </div>
                ))}

                {/* summary */}
                <div style={{ background:"#fff3ea", borderRadius:8, padding:12, marginTop:8 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:4 }}>
                    <span style={{ color:"#6b7280" }}>Taxable Amount</span>
                    <span style={{ fontWeight:600 }}>{INR(totals.taxable)}</span>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:4 }}>
                    <span style={{ color:"#6b7280" }}>{isInterState ? "IGST" : "CGST + SGST"}</span>
                    <span style={{ fontWeight:600 }}>{INR(totals.gst)}</span>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:15, fontWeight:700, borderTop:"1px solid #fed7aa", paddingTop:8, marginTop:8 }}>
                    <span>Grand Total</span>
                    <span style={{ color:"#F47920" }}>{INR(Math.round(totals.total))}</span>
                  </div>
                  {isInterState && (
                    <div style={{ fontSize:11, color:"#d97706", marginTop:6 }}>⚠ Inter-state supply — IGST applicable</div>
                  )}
                </div>
              </div>
            </div>

            {/* action buttons */}
            <div style={{ display:"flex", gap:10, marginBottom:14 }}>
              <button style={{ ...S.btn(), flex:1, padding:"10px" }} onClick={()=>setPreview(!preview)}>
                {preview ? "Hide Preview" : "👁 Preview Invoice"}
              </button>
              <button style={{ ...S.btn("#2563eb"), flex:1, padding:"10px" }} onClick={handlePrint}>
                🖨️ Print 4×6 Invoice
              </button>
            </div>

            {/* inline preview */}
            {preview && (
              <div style={{ border:"2px solid #e5e7eb", borderRadius:10, overflow:"hidden" }}>
                <div style={{ background:"#f9fafb", padding:"8px 14px", fontSize:11, color:"#6b7280", borderBottom:"1px solid #e5e7eb" }}>
                  Preview — {form.type === "proforma" ? "Proforma Invoice" : "Tax Invoice"} {form.invoiceNumber}
                </div>
                <div style={{ transform:"scale(0.75)", transformOrigin:"top left", width:"133%", pointerEvents:"none" }}>
                  <InvoiceHTML/>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* history tab */}
      {tab==="history" && (
        <div style={S.card}>
          <div style={S.cardHdr}><span>Invoice History</span></div>
          {invoices.length===0 ? (
            <div style={{ textAlign:"center", padding:50, color:"#9ca3af" }}>
              <div style={{ fontSize:36, marginBottom:10 }}>🧾</div>
              No invoices yet — create one from the Create Invoice tab
            </div>
          ) : (
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>
                  {["Invoice No","Date","Customer","Platform","Amount",""].map((h)=>(
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {invoices.map((inv,i)=>(
                    <tr key={i} onMouseEnter={(e)=>e.currentTarget.style.background="#fafafa"} onMouseLeave={(e)=>e.currentTarget.style.background=""}>
                      <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920" }}>{inv.id}</td>
                      <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{inv.date}</td>
                      <td style={{ ...S.td, fontWeight:500 }}>{inv.customer}</td>
                      <td style={S.td}>{inv.platform}</td>
                      <td style={{ ...S.td, fontWeight:700, color:"#F47920" }}>{INR(inv.total)}</td>
                      <td style={S.td}>
                        <button style={{ ...S.btn("#2563eb"), fontSize:11, padding:"3px 10px" }} onClick={()=>{
                          setForm((f)=>({...f, billingName:inv.customer, invoiceNumber:inv.id, platform:inv.platform,
                            items:[{description:inv.productName||"",hsn:BUSINESS.hsn,qty:inv.qty||1,unit:"Pcs",rate:Math.round((inv.total||0)/1.18/(inv.qty||1)),gstRate:18}]
                          }));
                          setTab("create");
                          toast("Invoice loaded ✓","success");
                        }}>Reprint</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* hidden print area */}
      <div style={{ display:"none" }}>
        <InvoiceHTML/>
      </div>
    </div>
  );
}
